import React, { useState, useRef, useCallback, useImperativeHandle, forwardRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { IntentHintMessage } from '../types/protocol';

interface ChatInputProps {
  onSend: (text: string) => void;
  onTyping?: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
  intentHint?: IntentHintMessage | null;
  onHintClick?: () => void;
  onDismissHint?: () => void;
  locale?: string;
  inWorkflow?: boolean;
}

export interface ChatInputHandle {
  focus: () => void;
}

// Check for Speech API support (vendor-prefixed in most browsers)
/* eslint-disable @typescript-eslint/no-explicit-any */
const SpeechRecognition: (new () => any) | undefined =
  typeof window !== 'undefined'
    ? (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition
    : undefined;
/* eslint-enable @typescript-eslint/no-explicit-any */

/** Map ACD locale codes (e.g. 'enUS') to BCP-47 tags for Web Speech API. */
function localeToBCP47(locale?: string): string {
  if (!locale) return 'en-US';
  const map: Record<string, string> = {
    enUS: 'en-US', esUS: 'es-US', frFR: 'fr-FR', ptBR: 'pt-BR',
    deDE: 'de-DE', itIT: 'it-IT', jaJP: 'ja-JP', koKR: 'ko-KR',
    zhCN: 'zh-CN', zhTW: 'zh-TW', arSA: 'ar-SA', hiIN: 'hi-IN',
    ruRU: 'ru-RU', viVN: 'vi-VN', tlPH: 'tl-PH',
  };
  return map[locale] ?? `${locale.slice(0, 2)}-${locale.slice(2)}`;
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(function ChatInput({ onSend, onTyping, disabled, placeholder, intentHint, onHintClick, onDismissHint, locale, inWorkflow }, ref) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recognitionRef = useRef<any>(null);

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
  }));

  // Cleanup recognition on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
        recognitionRef.current = null;
      }
    };
  }, []);

  const toggleListening = useCallback(() => {
    if (!SpeechRecognition) return;

    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = localeToBCP47(locale);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognition.onresult = (event: any) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setText(transcript);
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognition.onerror = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [isListening, locale]);

  const resizeTextarea = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 150) + 'px';
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = text.trim();
      if (!trimmed || disabled) return;
      onSend(trimmed);
      setText('');
      // Reset height after send
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.style.height = 'auto';
        }
      });
    },
    [text, disabled, onSend],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const val = e.target.value;
      setText(val);
      resizeTextarea();

      if (onTyping) {
        if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => {
          onTyping(val);
        }, 300);
      }
    },
    [onTyping, resizeTextarea],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Tab+Enter = newline (insert and prevent send)
      if (e.key === 'Enter' && e.metaKey) {
        // Do nothing — let browser insert newline
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        const trimmed = text.trim();
        if (trimmed && !disabled) {
          onSend(trimmed);
          setText('');
          requestAnimationFrame(() => {
            if (inputRef.current) {
              inputRef.current.style.height = 'auto';
            }
          });
        }
      }
    },
    [text, disabled, onSend],
  );

  return (
    <div className="sp-input-area">
      {intentHint && intentHint.confidence > 0.5 && (
        <div className="sp-intent-hint">
          <button type="button" className="sp-hint-action" onClick={() => { setText(''); onHintClick?.(); }}>
            {t('input.hintPrefix')} <strong>{intentHint.workflowName}</strong> {t('input.hintSuffix')}
          </button>
          <button type="button" className="sp-hint-dismiss" onClick={onDismissHint} aria-label={t('input.dismissHint')}>
            &times;
          </button>
        </div>
      )}
      <form className="sp-input-form" onSubmit={handleSubmit}>
        <textarea
          ref={inputRef}
          className="sp-input"
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder ?? t('input.placeholder')}
          rows={1}
          autoFocus
        />
        {SpeechRecognition && (
          <button
            className={`sp-mic-btn${isListening ? ' sp-mic-btn-active' : ''}`}
            type="button"
            onClick={toggleListening}
            disabled={disabled}
            aria-label={isListening ? t('input.stopVoice') : t('input.startVoice')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
          </button>
        )}
        <button
          className="sp-send-btn"
          type="submit"
          disabled={disabled || !text.trim()}
          aria-label={t('input.send')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </form>
      {inWorkflow && (
        <div className="sp-input-hints">
          {t('input.helpHints')}
        </div>
      )}
    </div>
  );
});
